import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../components/Icon";
import DatePicker from "../components/forms/DatePicker";
import AsignacionesEditor, { asignacionesDe, aPayload } from "../components/forms/AsignacionesEditor";
import FechasDeControl, { FechaDelProyecto } from "../components/widgets/FechasDeControl";
import { Avatar, Badge, BtnGhost, BtnPrimary, Barra } from "../components/Atoms";
import MenuMas from "../components/widgets/MenuMas";
import HistorialReciente from "../components/widgets/HistorialReciente";
import FlowView from "../components/flow/FlowView";
import CronologiaView from "../components/timeline/CronologiaView";
import KpisView from "../components/kpis/KpisView";
import {
  PJ_ESTADOS, PJ_PRIOS, PJ_NODOS, PJ_ROLES, PJ_ESTADOS_PROYECTO,
  ST_CLR, PR_CLR, PR_BG, EDGE_CLR,
} from "../constants";
import { fmtHours, ringClr, puedeEditarTarea, puedeEditarCampoTarea, puedeEditarProyecto,
  puedeCrearProyectos,
  pasosDeTrabajo, avancePasos, avanceEsfuerzo } from "../utils";
import { calendarioVsTrabajo, planMinutos, previsionFin } from "../kpisProyecto";
import { salidasDe, etiquetaPropia } from "../components/flow/flowGeometry";
import Campo from "../components/forms/Campo";

const inS = { padding: "5px 8px", borderRadius: 7, border: "1px solid var(--border)",
  fontSize: 12, fontFamily: "var(--font)", background: "var(--surface2)", color: "var(--text)" };

const NODO = Object.fromEntries(PJ_NODOS.map(n => [n.key, n]));

// Un indicador de la fila de cabecera. Local a esta pagina, como Panel lo es a
// KpisView: son tres cajas con contenidos distintos, y un componente generico
// acabaria con media docena de props booleanas.
function Tile({ rotulo, valor, sub, subColor, barra, title }) {
  return (
    <div title={title} style={{ border: "1px solid var(--border)", borderRadius: 10,
      background: "var(--surface)", padding: "5px 10px", minWidth: 0, overflow: "hidden",
      display: "flex", flexDirection: "column", justifyContent: "center", gap: 3 }}>
      {/* Mismo rotulo y misma cifra que las tarjetas del Dashboard (KpisView):
          9 px en versal con .08em para el nombre, y la cifra en DM Sans gruesa.
          DM Mono aqui desentonaba: en esta aplicacion la monoespaciada es de las
          columnas de horas, no de los indicadores. */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em",
        textTransform: "uppercase", color: "var(--text3)", whiteSpace: "nowrap",
        overflow: "hidden", textOverflow: "ellipsis" }}>{rotulo}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 5, whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: "var(--text)",
          lineHeight: 1 }}>{valor}</span>
        {sub && <span style={{ fontSize: 11, fontWeight: 700,
          color: subColor || "var(--text3)" }}>{sub}</span>}
      </div>
      {barra}
    </div>
  );
}

// Una fila de paso. Los campos que se pueden tocar dependen del permiso: quien
// no reorganiza el proyecto solo avanza su propio trabajo (estado, estimacion,
// nota de bloqueo), y el resto de controles aparecen deshabilitados con la
// explicacion en el title en vez de desaparecer, para que se entienda por que.
function FilaPaso({ t, proyecto, currentUser, teams, users = [], miembros = [],
                   ramas = [], minutos, prevision, festivos = {}, onCambiar, onBorrar }) {
  const [abierto, setAbierto] = useState(false);
  const editable = puedeEditarTarea(currentUser, t, proyecto, teams);
  const puede = campo => puedeEditarCampoTarea(currentUser, t, proyecto, campo, teams);
  // Repartir un paso es reorganizar el proyecto, no avanzar el trabajo propio:
  // va con el mismo permiso que mover fechas, igual que en el backend.
  const puedeAsignar = puedeEditarProyecto(currentUser, proyecto, teams);
  const nodo = NODO[t.kind] || NODO.tarea;
  const esTrabajo = (t.kind || "tarea") === "tarea";
  // Inicio y Fin no son trabajo NI se planifican: son los dos extremos del
  // flujo, y sus fechas son las del proyecto. El resto de nodos de control si
  // se planifican —el dia en que se decide un rombo es una fecha del proyecto—
  // y por eso conservan sus campos.
  const esDelimitador = t.kind === "inicio" || t.kind === "fin";

  const est = Number(t.estimacion_min) || 0;
  const real = Number(minutos) || 0;
  const ratio = est ? real / est : null;

  const hoy = new Date();
  const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
  // Vencer es no haber entregado a tiempo, asi que solo vence lo que se
  // entrega: un paso de trabajo y un hito. El nodo de Inicio salia en rojo y
  // "vencido" por tener una fecha vieja guardada, y ahi no habia nada que
  // entregar ni nadie a quien avisar.
  const vencida = (esTrabajo || t.kind === "hito") && t.due_date && t.due_date < hoyIso
    && t.status !== "Completada" && t.status !== "Cancelada";

  // El color de la fila. El mismo que el nodo lleva en el lienzo, para que la
  // lista y el diagrama no hablen idiomas distintos: el estado en los pasos de
  // trabajo, y en un nodo de control el color de su papel —el rombo pregunta, el
  // hito marca un punto— que es lo unico que un nodo de control tiene que decir.
  const COLOR_CONTROL = {
    inicio: "#22c55e", fin: "#6b7280", condicional: "#8b5cf6",
    hito: "#f59e0b", switch: "#8b5cf6",
  };
  const color = esTrabajo ? (ST_CLR[t.status] || "#6b7280")
    : (COLOR_CONTROL[t.kind] || "var(--text3)");

  return (
    <div className="cw-entry" style={{
      marginBottom: 6, position: "relative", overflow: "hidden",
      // Un nodo de control no es trabajo, y se nota: fondo apagado y el titulo
      // mas discreto. En una lista de pasos hay que poder saltarselos con la
      // vista para contar lo que de verdad hay que hacer.
      background: esTrabajo ? "var(--surface)" : "var(--surface2)",
    }}>
      {/* La franja de color. Es lo que permite leer el estado de toda la lista
          de un vistazo sin ir select por select, y ocupa un sitio que no le
          quita espacio a nada. */}
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
        background: color, borderRadius: "11px 0 0 11px" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 9, paddingLeft: 4 }}>
        {/* Plegado apunta a la DERECHA y desplegado hacia abajo, que es la
            convencion de una fila que se abre en su sitio —la misma que ya usa
            la fila de persona de Asistencias— y no la de un desplegable, que es
            la que habia. Un chevron hacia abajo en una fila cerrada se lee como
            "aqui hay una lista que elegir", no como "esto se abre". */}
        <button aria-label={abierto ? "Ocultar los detalles" : "Ver los detalles"} className="btn" onClick={() => setAbierto(a => !a)}
          title={abierto ? "Ocultar los detalles" : "Ver los detalles"}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
          <Icon name={abierto ? "chevron-down" : "chevron-right"} size={11} color="var(--text3)" />
        </button>

        {/* Un rombo se dibuja como rombo tambien aqui: girar el cuadrado 45
            grados cuesta una linea y hace que la lista y el lienzo se reconozcan
            como el mismo diagrama. */}
        <div title={nodo.label} style={{ width: 24, height: 24, flexShrink: 0,
          borderRadius: t.kind === "condicional" || t.kind === "hito" ? 4 : 6,
          transform: t.kind === "condicional" || t.kind === "hito" ? "rotate(45deg)" : undefined,
          background: color + "18",
          border: esTrabajo ? "none" : `1px solid ${color}55`,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={nodo.icon} size={11} color={color}
            style={t.kind === "condicional" || t.kind === "hito"
              ? { transform: "rotate(-45deg)" } : undefined} />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t.title}
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", display: "flex", gap: 6,
            flexWrap: "wrap", marginTop: 1 }}>
            {t.tipo && <span>{t.tipo}</span>}
            {/* Un delimitador enseña la fecha del PROYECTO, no la suya: la suya
                puede ser un resto de cuando estos nodos tenian campos de fecha,
                y contradecir al proyecto en la misma pantalla. */}
            {esDelimitador ? (
              <FechaDelProyecto kind={t.kind} proyecto={proyecto} prevision={prevision} />
            ) : (<>
              {t.start_date && <span>· {t.start_date}</span>}
              {t.due_date && (
                <span style={{ color: vencida ? "var(--red-text)" : undefined,
                  fontWeight: vencida ? 700 : 400 }}>
                  {t.start_date ? "→ " : "· "}{t.due_date}{vencida ? " (vencida)" : ""}
                </span>
              )}
            </>)}
            {!esTrabajo && <span>· nodo de control</span>}
          </div>
        </div>

        {/* Tiempo real frente a lo estimado. Un guion, nunca un 0: un cero se
            leeria como "no se ha trabajado" y no como "no hay dato". */}
        {esTrabajo && (
          <div style={{ fontSize: 11, fontFamily: "var(--mono)", textAlign: "right",
            minWidth: 74, color: "var(--text3)" }} title={est ? "Real / estimado" : "Sin estimacion"}>
            {real ? fmtHours(real / 60) : "—"}
            {est ? <span style={{ color: ratio > 1 ? "var(--red-text)" : "var(--text3)" }}>
              {" / "}{fmtHours(est / 60)}</span> : null}
          </div>
        )}

        {esTrabajo && (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {(t.asignados || []).slice(0, 3).map((a, i) => (
              <div key={a.user_id + a.rol} style={{ marginLeft: i ? -5 : 0 }}
                title={`${a.name} · ${a.rol}`}>
                <Avatar name={a.name} size={20}
                  color={a.rol === "encargado" ? "var(--accent)" : "var(--text3)"} />
              </div>
            ))}
          </div>
        )}

        {esTrabajo && (
          <select value={t.status} disabled={!puede("status")}
            title={puede("status") ? "Estado" : "Solo puedes cambiar el estado de tus propios pasos"}
            onChange={e => onCambiar(t.id, { status: e.target.value })}
            style={{ ...inS, width: 120, cursor: puede("status") ? "pointer" : "not-allowed",
              opacity: puede("status") ? 1 : .55,
              borderColor: (ST_CLR[t.status] || "var(--border)") + "88" }}>
            {PJ_ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        {editable && (
          <button aria-label="Eliminar paso" className="btn" onClick={() => onBorrar(t)} title="Eliminar paso"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 3 }}>
            <Icon name="trash3" size={11} color="var(--text3)" />
          </button>
        )}
      </div>

      {/* Las ramas de un rombo o un switch, tambien en la lista.
          Un condicional en una lista plana era una fila mas, con un titulo que
          es una pregunta y ninguna respuesta a la vista: habia que irse al
          lienzo para saber a donde lleva cada salida. Aqui se ven sin salir, con
          el color de su rama —el mismo verde y rojo que el diagrama— y el paso
          al que van. */}
      {!!ramas.length && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7,
          paddingLeft: 41 }}>
          {ramas.map(r => (
            <span key={r.clave} title={r.destino ? `Lleva a «${r.destino}»` : "Esta salida no va a ninguna parte"}
              style={{ display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 11, padding: "2px 9px", borderRadius: 20,
                border: `1px solid ${r.color}55`, background: r.color + "12",
                color: r.destino ? "var(--text2)" : "var(--text3)" }}>
              <span style={{ fontWeight: 700, color: r.color }}>{r.nombre}</span>
              <Icon name="arrow-right-short" size={11} color="var(--text3)" />
              {r.destino || <i>sin conectar</i>}
            </span>
          ))}
        </div>
      )}

      {abierto && (
        <div className="fi" style={{ marginTop: 10, paddingTop: 10,
          borderTop: "1px solid var(--border2)", display: "grid", gap: 9,
          gridTemplateColumns: "repeat(auto-fit,minmax(168px,1fr))" }}>

          {/* El titulo tambien se edita aqui. Era lo unico que obligaba a abrir
              el modal del lapiz, y por eso el lapiz ya no existe: desplegar un
              paso ofrece lo mismo que ofrecia el, sin cambiar de pantalla. */}
          <div className="cw-field" style={{ gridColumn: "1/-1" }}>
            <Campo><label>Título</label>
            <input defaultValue={t.title} disabled={!puede("title")} key={t.id + "-title"}
              onBlur={e => {
                const v = e.target.value.trim();
                if (v && v !== t.title) onCambiar(t.id, { title: v });
                else e.target.value = t.title;   // un titulo vacio no es un titulo
              }} /></Campo>
          </div>

          <div className="cw-field" style={{ gridColumn: "1/-1" }}>
            <Campo><label>Descripción</label>
            <textarea rows={2} defaultValue={t.description || ""}
              disabled={!puede("description")}
              onBlur={e => e.target.value !== (t.description || "")
                && onCambiar(t.id, { description: e.target.value })}
              placeholder="Que hay que hacer, y como" /></Campo>
          </div>

          {esTrabajo && (
            <>
              <div className="cw-field">
                <Campo><label>Prioridad</label>
                <select value={t.priority} disabled={!puede("priority")}
                  title={puede("priority") ? "" : "Replanificar es del responsable del proyecto"}
                  onChange={e => onCambiar(t.id, { priority: e.target.value })}>
                  {PJ_PRIOS.map(p => <option key={p} value={p}>{p}</option>)}
                </select></Campo>
              </div>
              <div className="cw-field">
                <Campo><label>Tipo de trabajo</label>
                <input defaultValue={t.tipo || ""} disabled={!puede("tipo")}
                  onBlur={e => e.target.value !== (t.tipo || "") && onCambiar(t.id, { tipo: e.target.value })}
                  placeholder="Desarrollo, reunion..." /></Campo>
              </div>
              <div className="cw-field">
                <Campo><label>Estimación (horas)</label>
                <input type="number" min={0} step={0.5}
                  defaultValue={est ? est / 60 : ""} disabled={!puede("estimacion_min")}
                  onBlur={e => {
                    const h = parseFloat(e.target.value);
                    const min = isNaN(h) ? null : Math.round(h * 60);
                    if (min !== (est || null)) onCambiar(t.id, { estimacion_min: min });
                  }} placeholder="sin estimar" /></Campo>
              </div>
            </>
          )}

          {/* Aqui el nativo iba con defaultValue + onBlur, que es lo que hay
              que hacer con un campo de texto donde se teclea. Un calendario no
              se teclea: se elige un dia y ya esta, asi que pasa a ser
              controlado y guarda en el momento. De paso desaparece el guardado
              que se perdia si cerrabas la fila sin sacar el foco. */}
          {esDelimitador ? (
            <div style={{ gridColumn: "1/-1" }}>
              <FechasDeControl kind={t.kind} proyecto={proyecto}
                prevision={prevision} festivos={festivos} />
            </div>
          ) : (<>
            <div className="cw-field">
              <Campo><label>Fecha de inicio</label>
              <DatePicker value={t.start_date || ""} nombre="Fecha de inicio"
                placeholder="Sin fecha" disabled={!puede("start_date")}
                max={t.due_date || ""}
                onChange={v => onCambiar(t.id, { start_date: v || null })} /></Campo>
            </div>
            <div className="cw-field">
              <Campo><label>Fecha de fin</label>
              <DatePicker value={t.due_date || ""} nombre="Fecha de fin"
                placeholder="Sin fecha" disabled={!puede("due_date")}
                refDate={t.start_date || ""} min={t.start_date || ""}
                onChange={v => onCambiar(t.id, { due_date: v || null })} /></Campo>
            </div>
          </>)}

          {t.kind === "condicional" && (
            <div className="cw-field" style={{ gridColumn: "1/-1" }}>
              <Campo><label>Condicion (la pregunta que se hace este nodo)</label>
              <input defaultValue={t.condicion || ""} disabled={!puede("condicion")}
                onBlur={e => e.target.value !== (t.condicion || "")
                  && onCambiar(t.id, { condicion: e.target.value })}
                placeholder="¿Lo aprueba el cliente?" /></Campo>
            </div>
          )}

          {(t.status === "Bloqueada" || t.nota_bloqueo) && (
            <div className="cw-field" style={{ gridColumn: "1/-1" }}>
              <Campo><label>Por que esta bloqueado</label>
              <input defaultValue={t.nota_bloqueo || ""} disabled={!puede("nota_bloqueo")}
                onBlur={e => e.target.value !== (t.nota_bloqueo || "")
                  && onCambiar(t.id, { nota_bloqueo: e.target.value })}
                placeholder="Que hace falta para desbloquearlo" /></Campo>
            </div>
          )}

          {/* Antes esto era una lista de nombres que solo se podia MIRAR, y para
              cambiarla habia que abrir el modal. Es el mismo control que usa el
              formulario de crear un paso, no una copia parecida. */}
          {esTrabajo && (
            <div className="cw-field" style={{ gridColumn: "1/-1" }}>
              <Campo><label>Quien lo hace y a quien preguntar</label>
              <AsignacionesEditor valor={asignacionesDe(t)} users={users}
                miembros={miembros} disabled={!puedeAsignar} alto={150}
                onChange={v => onCambiar(t.id, aPayload(v))} /></Campo>
            </div>
          )}

          {!editable && (
            <div style={{ gridColumn: "1/-1", fontSize: 11, color: "var(--text3)",
              background: "var(--surface2)", padding: "6px 9px", borderRadius: 7 }}>
              Este paso no esta asignado a ti y no llevas el proyecto, asi que solo lo ves.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProyectoPage({
  detalle, cargando, currentUser, teams = [], users = [], festivos = {},
  onVolver, onCambiarTarea, onBorrarTarea, onNuevoPaso, onCargarTiempo, onEditarProyecto, onEditarPaso,
  onGuardarProtocolo, onArchivar, onDesarchivar, onBorrar,
  onCrearPaso, onBorrarPasoDirecto,
  onCrearDependencia, onEditarDependencia, onBorrarDependencia, onGuardarPosiciones,
  showToast, crono, agendaTiposData = [], misPasos = [],
}) {
  // Pestaña dentro del proyecto. La cronologia y el dashboard llegan en las
  // fases siguientes; no se pintan pestañas vacias.
  const [tab, setTab] = useState("pasos");
  const [tiempo, setTiempo] = useState(null);
  const [errTiempo, setErrTiempo] = useState(false);
  const pedido = useRef(null);

  const p = detalle?.proyecto;
  const tareas = detalle?.tareas || [];

  // Las salidas de cada rombo y cada switch, con el paso al que llevan. Se
  // calcula una vez para toda la lista: hacerlo dentro de cada fila obligaria a
  // recorrer todas las dependencias por cada paso pintado.
  const ramasPorPaso = useMemo(() => {
    const deps = detalle?.dependencias || [];
    const titulo = {};
    for (const t of tareas) titulo[String(t.id)] = t.title;

    const out = {};
    for (const t of tareas) {
      const kind = t.kind || "tarea";
      if (kind !== "condicional" && kind !== "switch") continue;
      const salidas = kind === "condicional"
        ? [{ clave: "si", nombre: "Si", color: EDGE_CLR.si },
           { clave: "no", nombre: "No", color: EDGE_CLR.no }]
        : salidasDe(t).map(sa => ({ clave: sa.clave, nombre: sa.nombre, color: EDGE_CLR.normal }));

      out[String(t.id)] = salidas.map(sa => {
        const d = deps.find(x => String(x.from_task_id) === String(t.id)
          && (x.from_handle || "out") === sa.clave);
        return {
          ...sa,
          // La etiqueta escrita a mano manda sobre el nombre de la salida: si
          // alguien ha puesto "si pasa de 5.000 €", eso dice mas que "Si".
          nombre: etiquetaPropia(d || {}) || sa.nombre,
          destino: d ? titulo[String(d.to_task_id)] : null,
        };
      });
    }
    return out;
  }, [tareas, detalle?.dependencias]);

  // El tiempo real se pide una vez por proyecto. El patron `alive` es el de
  // ProjectsPage: si se cambia de proyecto mientras vuela la peticion, no se
  // pinta la respuesta del anterior.
  useEffect(() => {
    if (!p?.id || pedido.current === String(p.id)) return;
    pedido.current = String(p.id);
    let alive = true;
    setTiempo(null); setErrTiempo(false);
    onCargarTiempo?.(p.id, { agrupar: "tarea" }).then(r => {
      if (!alive) return;
      if (r) setTiempo(r); else setErrTiempo(true);
    });
    return () => { alive = false; };
  }, [p?.id]); // eslint-disable-line

  // Desglose por paso Y por dia: es lo que responde "¿cuando se trabajo?", que
  // el relleno de la barra no puede decir. Se pide aparte del agregado porque
  // solo lo usa la cronologia.
  const [porDia, setPorDia] = useState(null);
  useEffect(() => {
    if (!p?.id) return;
    let alive = true;
    setPorDia(null);
    onCargarTiempo?.(p.id, { agrupar: "tarea-dia" }).then(r => {
      if (alive && r) setPorDia(r);
    });
    return () => { alive = false; };
  }, [p?.id]); // eslint-disable-line

  const porDiaPorTarea = useMemo(() => {
    const m = {};
    for (const f of porDia?.filas || []) {
      if (!f.task_id || !f.entry_date) continue;
      (m[String(f.task_id)] = m[String(f.task_id)] || []).push({
        fecha: f.entry_date, minutes: f.minutes,
      });
    }
    return m;
  }, [porDia]);

  // Desglose por persona. Va aparte porque el permiso es distinto: a quien no
  // es admin ni gestor el servidor le devuelve solo su propia linea y marca la
  // respuesta como parcial, y eso hay que avisarlo en vez de esconderlo.
  const [tiempoPersona, setTiempoPersona] = useState(null);
  useEffect(() => {
    if (!p?.id) return;
    let alive = true;
    setTiempoPersona(null);
    onCargarTiempo?.(p.id, { agrupar: "persona" }).then(r => {
      if (alive && r) setTiempoPersona(r);
    });
    return () => { alive = false; };
  }, [p?.id]); // eslint-disable-line

  const minutosPorPersona = useMemo(() => {
    const m = {};
    for (const f of tiempoPersona?.filas || []) {
      if (f.user_id) m[String(f.user_id)] = f.minutes;
    }
    return m;
  }, [tiempoPersona]);

  // minutos reales por paso, para contrastarlos con lo estimado
  const minutosPorTarea = useMemo(() => {
    const m = {};
    (tiempo?.filas || []).forEach(f => { if (f.task_id) m[String(f.task_id)] = f.minutes; });
    return m;
  }, [tiempo]);

  // Horas imputadas al proyecto que no cuelgan de ningun paso. Se cuentan en el
  // total pero no en el avance de nadie, y hay que decirlo.
  const sinPaso = useMemo(
    () => (tiempo?.filas || []).filter(f => !f.task_id).reduce((s, f) => s + f.minutes, 0),
    [tiempo]
  );
  const totalMin = useMemo(
    () => (tiempo?.filas || []).reduce((s, f) => s + f.minutes, 0),
    [tiempo]
  );

  // Los tres indicadores de la cabecera. En un useMemo porque
  // calendarioVsTrabajo recorre los dias laborables uno a uno y un proyecto
  // largo son varios centenares de iteraciones por render.
  const indicadores = useMemo(() => {
    const p_ = detalle?.proyecto;
    if (!p_) return null;
    const ts    = detalle?.tareas || [];
    const trab  = pasosDeTrabajo(ts);
    const hechos = trab.filter(t => t.status === "Completada").length;
    // El mismo avance que usa salud(), para que este tile y la pestaña Dashboard
    // no se contradigan en la misma pantalla.
    const avance = avanceEsfuerzo(ts, minutosPorTarea) ?? avancePasos(ts);
    return {
      trab, hechos,
      avancePasos: avancePasos(ts),
      plan: planMinutos(ts),
      cal: calendarioVsTrabajo(p_, avance, festivos),
      avance,
    };
  }, [detalle, minutosPorTarea, festivos]);

  // Cuando va a acabar el proyecto de verdad. Aqui arriba y una sola vez: es una
  // pasada por el grafo entero y la piden los dos nodos de control, que son dos
  // filas cualesquiera de la lista.
  const prevision = useMemo(
    () => previsionFin(detalle?.tareas || [], detalle?.dependencias || [], {
      festivos, inicio: detalle?.proyecto?.start_date || null,
    }),
    [detalle, festivos]
  );

  if (cargando && !detalle) {
    return <div style={{ padding: 40, textAlign: "center", color: "var(--text3)" }}>Cargando...</div>;
  }
  if (!p) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ color: "var(--text3)", marginBottom: 14 }}>No se ha podido abrir el proyecto.</div>
        <BtnGhost onClick={onVolver}>Volver</BtnGhost>
      </div>
    );
  }

  const est = PJ_ESTADOS_PROYECTO.find(e => e.key === p.estado) || PJ_ESTADOS_PROYECTO[0];

  return (
    // Columna flex de alto EXACTO, no un div que crece con su contenido.
    //
    // El lienzo del flujo pedia su alto con `calc(100vh - 250px)`, y ese 250 es
    // un numero magico que no puede saber lo que de verdad hay encima: cabecera
    // de la aplicacion, pestañas de la seccion, cabecera del proyecto, los
    // avisos del tiempo real —que aparecen y desaparecen— y las pestañas del
    // proyecto. Cuando la suma pasaba del hueco disponible, el contenedor de
    // App.jsx (overflowY:auto) sacaba barra de desplazamiento: el lienzo ya
    // ocupa toda la pantalla, asi que desplazarlo no lleva a ninguna parte.
    //
    // Con height:100% aqui y flex:1 abajo, el lienzo recibe exactamente el
    // hueco que queda, sea el que sea. Y las pestañas que SI son largas —Pasos
    // y Dashboard— siguen desbordando y usando el scroll del padre, que ahi es
    // lo que se quiere.
    //
    // Por eso la fila de indicadores de debajo de la cabecera es de alto FIJO y
    // no envuelve: si se le deja crecer a dos filas en una pantalla estrecha, lo
    // que se lleva es el alto del lienzo.
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Cabecera */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 11, marginBottom: 14,
        flexWrap: "wrap" }}>
        <button aria-label="Volver a la lista de proyectos" className="btn" onClick={onVolver} title="Volver a la lista"
          style={{ ...inS, width: "auto", cursor: "pointer", padding: "7px 12px", background: "var(--surface)",
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>
          <Icon name="chevron-left" size={12} color="var(--text2)" />Proyectos
        </button>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, fontSize: 18, color: "var(--text)" }}>{p.name}</span>
            <Badge label={est.label} color={est.color} bg={est.color + "18"} />
            {p.prioridad && p.prioridad !== "Media" && (
              <Badge label={p.prioridad} color={PR_CLR[p.prioridad]} bg={PR_BG[p.prioridad]} />
            )}
            {!p.can_edit && <Badge label="Solo lectura" color="var(--text3)" bg="var(--surface2)" />}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 3,
            display: "flex", gap: 7, flexWrap: "wrap" }}>
            {p.team_name && <span>{p.team_name}</span>}
            {p.owner_name && <span>· lleva {p.owner_name}</span>}
            {p.start_date && <span>· {p.start_date}{p.end_date ? ` → ${p.end_date}` : ""}</span>}
          </div>
        </div>

        <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
          <HistorialReciente historial={detalle.historial || []} conProyecto={false} />
          {p.can_edit && (
            <>
              <BtnGhost onClick={() => onEditarProyecto?.(p)}>
                <Icon name="pencil" size={11} color="var(--text3)" />Editar
              </BtnGhost>
              {/* Lo que se hace de tarde en tarde va al menu: dos botones del mismo peso que
                  Editar y Nuevo paso hacian la cabecera una fila de cinco acciones iguales.
                  Guardar como protocolo solo tiene sentido en un proyecto de verdad (de un
                  protocolo no se saca otro, se duplica) y con algo que guardar. */}
              <MenuMas acciones={[
                !p.es_plantilla && onGuardarProtocolo && !!(detalle.tareas || []).length && {
                  clave: "protocolo", etiqueta: "Guardar como protocolo", icono: "journal-text",
                  titulo: "Guardar este flujo para poder repetirlo en otros proyectos",
                  onClick: () => onGuardarProtocolo(p, pasosDeTrabajo(detalle.tareas || []).length) },
                !p.archived && onArchivar && {
                  clave: "archivar", etiqueta: "Archivar", icono: "inbox",
                  titulo: "Sacarlo de la lista sin perder nada: se puede recuperar",
                  onClick: () => onArchivar(p) },
              ].filter(Boolean)} />
              <BtnPrimary onClick={onNuevoPaso}>
                <Icon name="plus-lg" size={11} color="#fff" />Nuevo paso
              </BtnPrimary>
            </>
          )}
        </div>
      </div>

      {/* Un proyecto archivado tiene que DECIRLO nada mas abrirlo. Si no, se
          trabaja sobre el sin saber que no sale en ninguna lista, y lo que se
          apunte ahi no lo va a mirar nadie. */}
      {p.archived && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          background: "var(--warn-bg)", border: "1px solid var(--warn-line)", color: "var(--warn-text)",
          borderRadius: 10, padding: "9px 12px", fontSize: 12, marginBottom: 12,
          flexShrink: 0 }}>
          <Icon name="inbox" size={13} color="#a16207" />
          <span style={{ flex: 1, minWidth: 200 }}>
            Archivado: no sale en la lista de proyectos. Sigue entero, con sus pasos y
            sus horas.
          </span>
          {p.can_edit && (
            <BtnGhost onClick={() => onDesarchivar?.(p)}
              title="Devolverlo a la lista de proyectos">
              <Icon name="arrow-counterclockwise" size={11} color="var(--text3)" />Recuperar
            </BtnGhost>
          )}
          {/* El borrado de verdad solo desde aqui, y solo a quien el servidor va
              a dejar de todos modos. No se ofrece en un proyecto vivo: hay que
              archivarlo primero, que es el paso reversible donde da tiempo a
              comprobar que no se esta tirando lo que no es. */}
          {p.can_edit && puedeCrearProyectos(currentUser) && (
            <button className="btn" onClick={() => onBorrar?.(p)}
              title="Borrarlo definitivamente, con sus pasos y su historial"
              style={{ padding: "6px 13px", borderRadius: 8, cursor: "pointer",
                fontSize: 12, fontWeight: 600, fontFamily: "var(--font)",
                border: "1px solid #fca5a5", background: "var(--red-bg)", color: "var(--red-text)" }}>
              <Icon name="trash3" size={11} color="var(--red-text)" /> Borrar definitivamente
            </button>
          )}
        </div>
      )}

      {/* Indicadores. Alto fijo y sin envolver a proposito: ver el comentario
          del contenedor. Si no hay nada que decir, no se gastan los 52px. */}
      {indicadores && (indicadores.trab.length > 0 || indicadores.plan || indicadores.cal) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))",
          gap: 8, height: 52, flexShrink: 0, marginBottom: 12 }}>

          <Tile rotulo="Pasos"
            valor={`${indicadores.hechos}/${indicadores.trab.length}`}
            sub={indicadores.avancePasos !== null ? `${indicadores.avancePasos}%` : null}
            title="Pasos completados sobre el total de pasos de trabajo"
            barra={<Barra valor={indicadores.avancePasos}
              color={ringClr(indicadores.avancePasos || 0)} alto={4} />} />

          <Tile rotulo="Horas"
            valor={totalMin ? fmtHours(totalMin / 60) : "—"}
            sub={indicadores.plan ? `/ ${fmtHours(indicadores.plan / 60)}` : "Sin plan"}
            subColor={indicadores.plan && totalMin > indicadores.plan ? "var(--red)" : undefined}
            title={indicadores.plan
              ? "Horas imputadas frente al plan"
              : "Nadie ha estimado ni el proyecto ni sus pasos"}
            barra={<Barra
              valor={indicadores.plan ? (totalMin / indicadores.plan) * 100 : null}
              color={indicadores.plan && totalMin > indicadores.plan ? "var(--red-text)" : "var(--accent)"}
              alto={4} />} />

          <Tile rotulo="Calendario vs trabajo"
            valor={indicadores.cal ? `${Math.round(indicadores.cal.esperado)}% ido` : "—"}
            sub={indicadores.cal?.etiqueta}
            subColor={indicadores.cal?.color}
            title={indicadores.cal
              ? "Trabajo hecho frente al calendario ya consumido. No cuenta pasos vencidos: eso es el desvio de plazo del Dashboard."
              : "Sin fechas de inicio y fin no hay calendario contra el que medir"}
            barra={<Barra valor={indicadores.avance}
              color={ringClr(indicadores.avance || 0)} alto={4}
              marca={indicadores.cal?.esperado ?? null} />} />
        </div>
      )}

      {/* Avisos honestos sobre el tiempo real */}
      {errTiempo && (
        <div style={{ background: "var(--warn-bg)", border: "1px solid var(--warn-line)", color: "var(--warn-text)",
          borderRadius: 10, padding: "9px 12px", fontSize: 11.5, marginBottom: 12,
          display: "flex", gap: 7, alignItems: "center" }}>
          <Icon name="exclamation-triangle-fill" size={12} color="#a16207" />
          No se ha podido leer el tiempo real del cuaderno. Los pasos se muestran solo con
          su planificacion: donde dice «—» no significa cero horas, significa que no hay dato.
        </div>
      )}
      {tiempo?.parcial && (
        <div style={{ background: "var(--surface2)", border: "1px solid var(--border)",
          color: "var(--text3)", borderRadius: 10, padding: "8px 12px", fontSize: 11,
          marginBottom: 12 }}>
          Las horas que ves son solo las tuyas: el desglose del resto del equipo es de
          administracion, igual que en Asistencias.
        </div>
      )}
      {!!sinPaso && (
        <div style={{ background: "var(--surface2)", border: "1px solid var(--border)",
          borderRadius: 10, padding: "8px 12px", fontSize: 11, marginBottom: 12,
          color: "var(--text2)" }}>
          <b>{fmtHours(sinPaso / 60)}</b> imputadas a este proyecto sin colgar de ningun paso:
          cuentan en el total pero no en el avance de ninguno.
        </div>
      )}

      {/* Pestañas del proyecto */}
      <div className="pj-tabs" style={{ marginBottom: 12 }}>
        {[["pasos", "list-ul", "Pasos"], ["flujo", "diagram-3", "Flujo"],
          ["cronologia", "calendar-week", "Cronología"],
          ["kpis", "graph-up-arrow", "Resumen"]].map(([k, ic, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={"pj-tab" + (tab === k ? " on" : "")}>
            <Icon name={ic} size={12} color={tab === k ? "var(--accent)" : "var(--text3)"} />
            {label}
          </button>
        ))}
      </div>

      {tab === "flujo" && (
        // minHeight:0 es la pieza que hace que esto funcione: un hijo flex trae
        // min-height:auto por omision y se niega a encogerse por debajo de su
        // contenido, asi que sin el, el lienzo empuja la columna hacia abajo y
        // reaparece la barra que estamos quitando.
        <div style={{ flex: 1, minHeight: 0 }}>
          <FlowView
            proyecto={p} tareas={tareas} dependencias={detalle.dependencias || []}
            currentUser={currentUser} teams={teams}
            minutosPorTarea={minutosPorTarea}
            prevision={prevision} festivos={festivos}
            onCrearPaso={onCrearPaso}
            onCambiarPaso={onCambiarTarea}
            onBorrarPaso={onBorrarPasoDirecto}
            onCrearDependencia={onCrearDependencia}
            onEditarDependencia={onEditarDependencia}
            onBorrarDependencia={onBorrarDependencia}
            onGuardarPosiciones={onGuardarPosiciones}
            crono={crono} agendaTiposData={agendaTiposData} misPasos={misPasos}
            showToast={showToast} />
        </div>
      )}

      {tab === "kpis" && (
        <KpisView
          proyecto={p} tareas={tareas} dependencias={detalle.dependencias || []}
          users={users} festivos={festivos}
          minutosPorTarea={minutosPorTarea}
          minutosPorPersona={minutosPorPersona}
          porDia={porDia?.filas || []}
          tiempoParcial={!!tiempoPersona?.parcial} tiempoError={errTiempo}
          onAbrirPaso={t => onEditarPaso?.(tareas.find(x => String(x.id) === String(t.id)) || t)} />
      )}

      {tab === "cronologia" && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <CronologiaView
            proyecto={p} tareas={tareas} dependencias={detalle.dependencias || []}
            festivos={festivos}
            minutosPorTarea={minutosPorTarea}
            porDiaPorTarea={porDiaPorTarea}
            tiempoParcial={!!tiempo?.parcial} tiempoError={errTiempo}
            onAbrirPaso={onEditarPaso} />
        </div>
      )}

      {/* Pasos */}
      {/* La lista va dentro de un bloque que NO encoge. El contenedor de arriba
          es una columna flex de alto exacto, y ahi un hijo flex encoge si el
          contenido no cabe; una fila desplegada lleva `overflow:hidden`, asi que
          el navegador se cree que puede recortarla a cualquier alto y lo hace:
          el paso abierto se cortaba por la mitad. Esta pestaña desborda a
          proposito y se lee con el scroll del padre, que es lo que dice el
          comentario del contenedor. */}
      {tab === "pasos" && (<div style={{ flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
          letterSpacing: ".08em", color: "var(--text3)" }}>
          Pasos del proyecto
        </span>
        <span style={{ fontSize: 11, color: "var(--text3)" }}>({tareas.length})</span>
      </div>

      {!tareas.length && (
        <div style={{ padding: "36px 20px", textAlign: "center", background: "var(--surface)",
          border: "1px dashed var(--border)", borderRadius: 12 }}>
          <Icon name="signpost-split" size={26} color="var(--text3)" />
          <div style={{ fontWeight: 700, fontSize: 14, marginTop: 10, color: "var(--text2)" }}>
            Este proyecto no tiene pasos todavia
          </div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 5, lineHeight: 1.6 }}>
            Un paso es una tarea con sus fechas, su responsable y su encargado.
            {p.can_edit ? " Añade el primero para empezar." : ""}
          </div>
          {p.can_edit && (
            <div style={{ marginTop: 13, display: "flex", justifyContent: "center" }}>
              <BtnPrimary onClick={onNuevoPaso}>
                <Icon name="plus-lg" size={11} color="#fff" />Nuevo paso
              </BtnPrimary>
            </div>
          )}
        </div>
      )}

      {tareas.map(t => (
        <FilaPaso key={t.id} t={t} proyecto={p} currentUser={currentUser} teams={teams}
          minutos={minutosPorTarea[String(t.id)]}
          prevision={prevision} festivos={festivos}
          users={users} miembros={detalle.miembros || []}
          ramas={ramasPorPaso[String(t.id)] || []}
          onCambiar={onCambiarTarea} onBorrar={onBorrarTarea} />
      ))}

      {/* El total, que hasta ahora no estaba en ninguna pantalla: cada fila
          decia su real/estimado y nadie sumaba la columna. Las horas se cuentan
          del proyecto entero, asi que se dice cuantas no cuelgan de ningun paso
          en vez de dejar que el total no cuadre con las filas de arriba. */}
      {!!indicadores && indicadores.trab.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 4,
          padding: "9px 9px 9px 35px", borderTop: "2px solid var(--border)",
          fontSize: 11.5, color: "var(--text2)" }}>
          <span style={{ fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
            fontSize: 11 }}>Total</span>
          <span style={{ color: "var(--text3)" }}>
            {indicadores.trab.length} {indicadores.trab.length === 1 ? "paso" : "pasos"}
            {sinPaso > 0 && ` · ${fmtHours(sinPaso / 60)} sin paso`}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontFamily: "var(--mono)", fontWeight: 700 }}
            title={indicadores.plan ? "Horas imputadas / plan" : "Sin plan con el que comparar"}>
            {totalMin ? fmtHours(totalMin / 60) : "—"}
            {indicadores.plan && (
              <span style={{ color: totalMin > indicadores.plan ? "var(--red-text)" : "var(--text3)" }}>
                {" / "}{fmtHours(indicadores.plan / 60)}
              </span>
            )}
          </span>
        </div>
      )}

      {/* Equipo del proyecto. Solo en la pestaña de Pasos: en el lienzo y en la
          cronologia el area tiene alto fijo, asi que cualquier cosa debajo no
          aporta nada y solo hace que la pagina tenga scroll. Y en el Dashboard
          el reparto de carga ya dice quien esta en el proyecto. */}
      {!!(detalle.miembros || []).length && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".08em", color: "var(--text3)", marginBottom: 8 }}>
            Equipo del proyecto
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {PJ_ROLES.map(r => {
              const gente = (detalle.miembros || []).filter(m => m.rol === r.key);
              if (!gente.length) return null;
              return (
                <div key={r.key} style={{ background: "var(--surface)", border: "1px solid var(--border)",
                  borderRadius: 11, padding: "10px 13px", minWidth: 170 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: ".07em", color: "var(--text3)", marginBottom: 6 }}>
                    {r.label} <span style={{ fontWeight: 400, textTransform: "none" }}>· {r.sub}</span>
                  </div>
                  {gente.map(m => (
                    <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 7,
                      fontSize: 12, color: "var(--text2)", padding: "3px 0" }}>
                      <Avatar name={m.name} size={22}
                        color={r.key === "encargado" ? "var(--accent)" : "var(--text3)"} />
                      {m.name}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>)}
    </div>
  );
}
